/**
 * Оборотно-сальдова відомість (ч.1036 — переписано за каноном).
 *
 * Ієрархія рахунків (36 = 361 + 362 …) зі згортанням, розгорнуте сальдо по
 * субконто, позабалансові окремим блоком, перевірка Дт = Кт, друк і Excel.
 * Клік по рахунку — ОСВ по рахунку (ланцюжок 1С: ОСВ → ОСВ по рахунку →
 * картка → документ). Стан — в адресі (useLedgerParams).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports, type TrialBalanceRow } from '@/api/documents';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import LedgerFilters, { NoRegulatedAccess } from './ledger/LedgerFilters';
import { useLedgerParams } from './ledger/useLedgerParams';
import { balanceColumns, periodLabel, quantityColumns, TreeLabel, LABEL_W } from './ledger/ledgerColumns';
import {
  ledgerLinks, money, splitOffBalance, toggleCollapsed, visibleRows,
} from './ledger/ledgerModel';

type Row = TrialBalanceRow & { _kind?: 'total' | 'off-total' | 'section' };

const TITLE = 'Оборотно-сальдова відомість';

function synthetic(kind: NonNullable<Row['_kind']>, name: string, b?: Partial<TrialBalanceRow>): Row {
  return {
    account_code: '', account_name: name, account_type: 'AP', parent_code: null, level: 0,
    is_group: false, is_off_balance: kind === 'off-total', has_subconto: false,
    opening_dt: '0', opening_cr: '0', turnover_dt: '0', turnover_cr: '0', closing_dt: '0', closing_cr: '0',
    ...b, _kind: kind,
  };
}

export default function TrialBalancePage() {
  const st = useLedgerParams();
  const navigate = useNavigate();
  const expanded = st.get('expanded') === '1';
  const withQty = st.get('qty') === '1';
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ['ledger-tb', st.org, st.from, st.to, expanded, withQty],
    queryFn: () => reports.trialBalance({
      organization_id: st.org, date_from: st.from, date_to: st.to, expanded, with_quantity: withQty }),
    enabled: !!st.org,
  });
  const data = q.data;
  const period = { org: st.org, from: st.from, to: st.to };
  const key = (r: Row) => r.account_code;

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const { balance, off } = splitOffBalance(data.rows);
    const out: Row[] = [...visibleRows(balance, collapsed, key)];
    out.push(synthetic('total', 'Разом', data.totals));
    if (off.length) {
      // Позабалансові і допоміжний 00 (зустрічна сторона позабалансових у клоні
      // 1С) — поза перевіркою Дт = Кт, тому окремим блоком.
      out.push(synthetic('section', 'Позабалансові та допоміжні рахунки'));
      out.push(...visibleRows(off, collapsed, key));
      out.push(synthetic('off-total', 'Разом поза балансом', data.off_balance_totals));
    }
    return out;
  }, [data, collapsed]);

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    {
      key: 'account', header: 'Рахунок', width: LABEL_W,
      cell: (r) => r._kind ? <strong>{r.account_name}</strong> : (
        <TreeLabel level={r.level} isGroup={r.is_group} collapsed={collapsed.has(r.account_code)}
          onToggle={() => setCollapsed((s) => toggleCollapsed(s, r.account_code))}>
          <a className="ledger__link" title="ОСВ по рахунку: по субконто"
            onClick={(e) => { e.stopPropagation(); navigate(ledgerLinks.trialBalanceAccount({ ...period, account: r.account_code })); }}>
            {r.account_code}
          </a>
          <span>{r.account_name}</span>
        </TreeLabel>
      ),
    },
    ...balanceColumns<Row>(),
    ...(withQty ? quantityColumns<Row>() : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [collapsed, withQty, st.org, st.from, st.to]);

  if (!st.permsLoading && !st.hasPermission) {
    return <NoRegulatedAccess title={TITLE} total={st.totalAccessibleOrgs} />;
  }
  const groups = data ? data.rows.filter((r) => r.is_group).map(key) : [];

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{TITLE}
          {' '}<span className="ledger__subtitle">{periodLabel(st.from, st.to)}</span>
        </h2>
      </div>

      <LedgerFilters state={st}
        printHref={ledgerLinks.print('tb', { ...period, expanded: expanded ? '1' : undefined, qty: withQty ? '1' : undefined })}
        onExcel={() => reports.ledgerXlsx('/reports/trial-balance', {
          organization_id: st.org, date_from: st.from, date_to: st.to, expanded, with_quantity: withQty })}
        disabled={!data}>
        <label className="ledger__check" title="Сальдо по субконто окремо Дт і Кт: борги покупців і їх переплати не згортаються">
          <input type="checkbox" checked={expanded}
            onChange={(e) => st.set({ expanded: e.target.checked ? '1' : null })} />
          розгорнуте сальдо
        </label>
        <label className="ledger__check" title="Кількість по кількісних рахунках (запаси, товари, продукція)">
          <input type="checkbox" checked={withQty}
            onChange={(e) => st.set({ qty: e.target.checked ? '1' : null })} />
          кількість
        </label>
        <span className="ledger__tree-actions">
          <button type="button" className="btn-link" onClick={() => setCollapsed(new Set(groups))}>згорнути групи</button>
          <button type="button" className="btn-link" onClick={() => setCollapsed(new Set())}>розгорнути</button>
        </span>
      </LedgerFilters>

      {q.isError && <p className="ledger__warn">Не вдалося завантажити звіт. Оновіть сторінку або повторіть вхід.</p>}

      <DataTable<Row>
        rows={rows}
        columns={cols}
        rowKey={(r) => r._kind ? `_${r._kind}` : r.account_code}
        loading={q.isLoading}
        emptyText={st.org ? 'За період проводок немає.' : 'Оберіть організацію.'}
        rowBold={(r) => r.is_group || !!r._kind}
        rowBackground={(r) => r._kind ? 'var(--bg-soft)' : undefined}
        onRowDoubleClick={(r) => { if (!r._kind) navigate(ledgerLinks.trialBalanceAccount({ ...period, account: r.account_code })); }}
        storageKey="ledger-trial-balance"
      />

      {data && (
        <div className="ledger__totals">
          <span>Обороти: Дт <strong>{money(data.totals.turnover_dt, { zero: '0,00' })}</strong> · Кт <strong>{money(data.totals.turnover_cr, { zero: '0,00' })}</strong></span>
          <span className={data.balanced ? 'ledger__ok' : 'ledger__bad'}>
            {data.balanced ? '✓ баланс сходиться' : '✗ баланс не сходиться — перевірте проводки'}
          </span>
          <span className="ledger__hint">клік по рахунку — ОСВ по рахунку; подвійний клік по рядку — те саме</span>
        </div>
      )}
    </div>
  );
}
